import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  Circle,
  CircleCheck,
  HeartHandshake,
  Loader2,
  Lock,
  Plus,
  Sun,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { LogoMark } from "@/components/logo";
import { NoLiteratureNote, StepReadings } from "@/components/recovery/step-readings";
import {
  ApiError,
  addReflectionAddition,
  addStepReflection,
  getRelationships,
  getStep,
  startStep,
  updateStep,
  type OutgoingShare,
  type StepData,
  type StepReflection,
} from "@/lib/api";

/**
 * The per-step flow (template for all twelve): overview → reading →
 * reflection → willingness → sponsor → close. Per §8: NO AI, readings are
 * citations only, reflections append-only. Progressive reveal is enforced
 * server-side; this screen renders the locked state calmly.
 */

const PHASES = ["overview", "reading", "reflection", "willingness", "sponsor", "close"] as const;
type Phase = (typeof PHASES)[number];

const WORDS = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];

// Per-step voice where a phase needs its own wording; generic fallbacks keep
// steps 3–12 working the moment their catalog rows land.
const WILLINGNESS: Record<number, { title: string; body: string; note?: string }> = {
  1: {
    title: "Willingness",
    body: "This step asks only for willingness — not certainty. If it helps, note where you are right now.",
  },
  2: {
    title: "What would it take for you to stay open a little longer?",
    body: "Only if something comes to mind. It's fine to skip this.",
  },
  3: {
    title: "What are you willing to let go of today, even a little?",
    body: "Only if something comes to mind. It's fine to skip this, or to say you're not ready.",
  },
  6: {
    title: "What are you willing to have lifted from you today, even a little?",
    body: "Only if something comes to mind. It's fine to skip this, or to say you're not ready.",
    note: "This step is about being willing to have these patterns removed by a power greater than yourself — however you understand that. The app has nothing to add about what that is; that part is yours and stays in your own words.",
  },
  // Step 7 is the asking itself (its eyebrow label comes from the catalog).
  7: {
    title: "If you want to put the asking in your own words, this is the place.",
    body: "Only if something comes to mind. It's fine to skip this, or to say you don't know how to ask.",
    note: "Step 7 is asking that these be lifted — asking a power greater than yourself, however you understand that. The app has nothing to add about who or what that is, or about what asking should look like. Those words are yours.",
  },
};
// Optional per-step affirmation on the Willingness screen (Step 3 canvas).
// Leaving it unchecked is just as honest — it gates nothing.
const AFFIRMATION: Record<number, { label: string; sub: string }> = {
  3: {
    label: "I'm ready to make this decision today",
    sub: "And it's just as honest to leave this unchecked.",
  },
  6: {
    label: "I'm ready today",
    sub: "And it's just as honest to leave this unchecked. Readiness comes in its own time.",
  },
  7: {
    label: "I've asked, in my own way",
    sub: "And it's just as honest to leave this unchecked. “I'm not sure yet” is an answer.",
  },
};
const SPONSOR_BODY: Record<number, string> = {
  2: "Belief isn't something to settle alone. Share what you've written, or simply let your sponsor know you're ready to talk it through — doubt and all.",
  3: "Some people make this decision with their sponsor, in their own words. Share what you've written, or simply let your sponsor know you're ready to talk it through.",
  6: "Readiness is easier to hear out loud than to decide alone. Share what you've written, or simply let your sponsor know you're ready to talk it through.",
  7: "Share what you've written, or simply let your sponsor know you've done Step 7 and want to talk it through.",
};
// An extra note on the sponsor hand-off screen, where a step needs to say
// exactly what sharing includes before the choice is made (Step 7 canvas §6).
// Rendered with a lock icon. Step 7's asking is the most personal screen in
// the step, so sharing it is the sponsee's to give — knowingly.
const SPONSOR_PRIVACY_NOTE: Record<number, string> = {
  7: "Sharing your reflections includes what you wrote on the asking screen — the most personal part of this step. If you'd rather keep that to yourself, choose the second option; nothing you wrote is sent.",
};
const CLOSE_BODY: Record<number, string> = {
  1: "That took honesty. Whenever you're ready, Step 2 is open — no rush, and nothing behind you disappears. One day at a time.",
  2: "Staying open, even with the doubt, is its own kind of courage. Your reflections are here whenever you want them. Step 3 will be waiting when you and your sponsor are ready.",
  3: "A decision like this gets made again most mornings. Your words are here whenever you want them. Step 4 will be waiting when you and your sponsor are ready.",
  6: "Nothing has to change today. Willingness is the whole of this step, and you can come back and add to what you wrote at any time. Step 7 will be waiting when you and your sponsor are ready.",
  7: "Nothing has to feel different today. You asked, in your own words, and you can come back and add to what you wrote at any time. Step 8 will be waiting when you and your sponsor are ready.",
};
// A gentler close for steps with an affirmation, when the person honestly
// left it unchecked (Step 6 canvas): not being ready yet is its own answer.
const CLOSE_PARTIAL: Record<number, { title: string; body: string }> = {
  6: {
    title: "Ready for some of it is ready.",
    body: "You named what you're not ready to let go of, and that's honest work. Come back to it whenever you want — the page stays open. Nothing here is behind schedule.",
  },
  7: {
    title: "Not knowing how to ask is a way of asking.",
    body: "You wrote it down without being sure who you were saying it to. That counts. Come back and add to it whenever you want — the page stays open.",
  },
};

export default function StepFlow() {
  const navigate = useNavigate();
  const { n = "1" } = useParams();
  const stepNumber = Math.max(1, Math.min(12, Number(n) || 1));

  const [data, setData] = useState<StepData | null>(null);
  const [phase, setPhase] = useState<Phase>("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [readOn, setReadOn] = useState("");
  const [readingNote, setReadingNote] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [willingness, setWillingness] = useState("");
  const [affirmed, setAffirmed] = useState(false);

  // Sponsor hand-off state.
  const [sponsors, setSponsors] = useState<OutgoingShare[]>([]);
  const [shareChoice, setShareChoice] = useState<"share" | "nudge">("share");

  async function load() {
    try {
      const d = await getStep(stepNumber);
      setData(d);
      if (d.work?.current_phase && (PHASES as readonly string[]).includes(d.work.current_phase)) {
        setPhase(d.work.current_phase as Phase);
      } else {
        setPhase("overview");
      }
      setReadOn(d.work?.reading_read_on ?? "");
      setReadingNote(d.work?.reading_impressions ?? "");
      setAcknowledged(!!d.work?.reading_acknowledged);
      setWillingness(d.work?.willingness_note ?? "");
      setAffirmed(Boolean(d.work?.willingness_affirmed));
      if (d.work) setShareChoice(d.work.is_shared ? "share" : "nudge");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError(`Couldn't load Step ${stepNumber}. Please try again.`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepNumber]);

  // Sponsor list, loaded when the hand-off phase is reached.
  useEffect(() => {
    if (phase !== "sponsor") return;
    getRelationships()
      .then((r) =>
        setSponsors(r.outgoing.filter((o) => o.role === "sponsor" && o.status === "active"))
      )
      .catch(() => {});
  }, [phase]);

  async function goTo(next: Phase, patch: Record<string, unknown> = {}) {
    setBusy(true);
    try {
      if (!data?.work) await startStep(stepNumber);
      await updateStep(stepNumber, { current_phase: next, ...patch });
      await load();
      setPhase(next);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else if (e instanceof ApiError && e.status === 403) setError(e.message);
      else setError("Couldn't save your progress. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const phaseIndex = PHASES.indexOf(phase);
  const word = WORDS[stepNumber];
  // Steps with an affirmation get a gentler close when it was left unchecked:
  // "not ready yet" is an honest answer, not an incomplete one.
  const partialClose =
    AFFIRMATION[stepNumber] && !data?.work?.willingness_affirmed
      ? CLOSE_PARTIAL[stepNumber]
      : undefined;

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "Not available."}</p>
        <Button variant="outline" onClick={() => navigate("/steps")}>
          Back to Steps
        </Button>
      </div>
    );
  }

  const { step, prompts } = data;

  /* ------------------------------ locked ------------------------------ */
  if (!data.available) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <Lock className="h-6 w-6 text-muted-foreground" strokeWidth={2} />
        <div>
          <h1 className="text-xl font-bold text-foreground">Step {stepNumber} — not yet</h1>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            It opens when you mark Step {stepNumber - 1} ready. There's no rush to get
            there.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/steps")}>
          Back to Steps
        </Button>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <div className="flex items-center gap-3 px-4 pb-2 pt-6">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Back"
          onClick={() => {
            if (phaseIndex <= 0) navigate("/steps");
            else setPhase(PHASES[phaseIndex - 1]);
          }}
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <div className="flex-1">
          <Progress value={Math.round(((phaseIndex + 1) / PHASES.length) * 100)} />
        </div>
      </div>

      <div className="animate-fade-rise px-6 pt-4">
        {/* ----------------------------- overview ----------------------------- */}
        {phase === "overview" && (
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Step {word}
                {step.principle ? ` · ${step.principle}` : ""}
              </p>
              <h1 className="mt-1 text-2xl font-bold text-foreground">{step.name}</h1>
            </div>
            {step.intro_text && (
              <p className="text-[15px] leading-relaxed text-foreground">{step.intro_text}</p>
            )}
            {step.supporting_note && (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
                <Sun className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed text-foreground">
                  {step.supporting_note}
                </p>
              </div>
            )}
            {step.privacy_note && (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
                <Lock className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed text-foreground">{step.privacy_note}</p>
              </div>
            )}
            {/* Resolved from the user's own literature (Fellowships PRD §10.2–3);
                empty renders "your own reading" rather than nothing. */}
            <StepReadings citations={data.citations} />
            {data.work && (
              <Card>
                <CardContent className="flex flex-col gap-1.5 px-4 py-3.5">
                  <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Where you left off
                  </p>
                  {data.work.reading_acknowledged ? (
                    <p className="flex items-center gap-2 text-sm text-foreground">
                      <Check className="h-3.5 w-3.5" strokeWidth={2} /> Reading — noted
                    </p>
                  ) : null}
                  {data.reflections.length > 0 && (
                    <p className="flex items-center gap-2 text-sm text-foreground">
                      <Check className="h-3.5 w-3.5" strokeWidth={2} /> Reflection —{" "}
                      {data.reflections.length} answer
                      {data.reflections.length === 1 ? "" : "s"} saved
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
            <Button size="xl" fullWidth disabled={busy} onClick={() => goTo("reading")}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : data.work ? (
                `Continue Step ${stepNumber}`
              ) : (
                `Begin Step ${stepNumber}`
              )}
            </Button>
            {/* The self-appraisal is the Johns Hopkins ALCOHOL set, so it is
                offered only to someone using AA's literature — and adapting it
                would make it wrong for everyone else (Fellowships PRD §7).
                Absence is a fine outcome, said plainly. */}
            {stepNumber === 1 && !data.work && data.fellowships.includes("aa") && (
              <button
                type="button"
                onClick={() => navigate("/twenty-questions")}
                className="text-center text-[13px] text-muted-foreground underline underline-offset-2"
              >
                Optional: take the Twenty Questions first
              </button>
            )}
            {stepNumber === 1 && !data.work && data.fellowships.length > 0 &&
              !data.fellowships.includes("aa") && (
                <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary px-4 py-3">
                  <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                  <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                    There's no self-appraisal offered for the literature you use. The one Happy24
                    carries is alcohol-specific, and adapting it would make it wrong for you. Step 1
                    stands on its own.
                  </p>
                </div>
              )}
            <p className="text-center text-xs text-muted-foreground">
              You can leave and come back at any point. Everything you write is private
              unless you choose to share it.
            </p>
          </div>
        )}

        {/* ------------------------------ reading ----------------------------- */}
        {phase === "reading" && (
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Step {word} · Reading
              </p>
              <h2 className="mt-1 text-xl font-semibold text-foreground">The reading</h2>
            </div>
            <StepReadings citations={data.citations} />
            {data.citations.length === 0 && <NoLiteratureNote />}
            {step.reading_note && (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
                <BookOpen className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed text-foreground">{step.reading_note}</p>
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                When did you read it?{" "}
                <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Input
                placeholder="e.g. July 20"
                value={readOn}
                onChange={(e) => setReadOn(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                First impressions{" "}
                <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Textarea
                rows={4}
                placeholder="A word, a line, a feeling…"
                value={readingNote}
                onChange={(e) => setReadingNote(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => setAcknowledged((v) => !v)}
              aria-pressed={acknowledged}
              className={`flex items-center gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
                acknowledged ? "border-2 border-primary bg-primary/[0.06]" : "border-border"
              }`}
            >
              <span className={acknowledged ? "text-primary" : "text-muted-foreground"}>
                {acknowledged ? (
                  <CircleCheck className="h-5 w-5" strokeWidth={2} />
                ) : (
                  <Circle className="h-5 w-5" strokeWidth={2} />
                )}
              </span>
              <span>
                <span className="block text-sm font-medium text-foreground">I've read this</span>
                <span className="block text-xs text-muted-foreground">
                  Just honesty with yourself — nothing is unlocked or earned.
                </span>
              </span>
            </button>
            <Button
              size="xl"
              fullWidth
              disabled={busy}
              onClick={() =>
                goTo("reflection", {
                  reading_read_on: readOn.trim() || null,
                  reading_impressions: readingNote.trim() || null,
                  reading_acknowledged: acknowledged,
                })
              }
            >
              Continue
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>
        )}

        {/* ---------------------------- reflection ---------------------------- */}
        {phase === "reflection" && (
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Step {word} · Reflection
              </p>
              <h2 className="mt-1 text-xl font-semibold text-foreground">Reflect</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Write freely — no one sees this unless you choose to share it. You can
                always add more later.
              </p>
            </div>
            {prompts.map((p) => (
              <PromptBlock
                key={p.id}
                stepNumber={stepNumber}
                promptId={p.id}
                promptText={p.prompt_text}
                note={p.note_text}
                answers={data.reflections.filter((r) => r.step_prompt_id === p.id)}
                onSaved={load}
                onError={setError}
              />
            ))}
            <Button size="xl" fullWidth disabled={busy} onClick={() => goTo("willingness")}>
              Continue
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>
        )}

        {/* ---------------------------- willingness --------------------------- */}
        {phase === "willingness" && (
          <div className="flex flex-col gap-4">
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              {/* The fourth phase's name is content, not chrome: Steps 1–3 and 6
                  call it Willingness, Step 7 calls it The asking. */}
              Step {word} · {step.willingness_label ?? "Willingness"}{" "}
              <span className="normal-case tracking-normal">(optional)</span>
            </p>
            <h2 className="text-xl font-semibold text-foreground">
              {(WILLINGNESS[stepNumber] ?? WILLINGNESS[1]).title}
            </h2>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              {(WILLINGNESS[stepNumber] ?? WILLINGNESS[1]).body}
            </p>
            {WILLINGNESS[stepNumber]?.note && (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
                <Sun className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed text-foreground">
                  {WILLINGNESS[stepNumber].note}
                </p>
              </div>
            )}
            <Textarea
              rows={6}
              placeholder="In your own words… (optional)"
              value={willingness}
              onChange={(e) => setWillingness(e.target.value)}
            />
            {AFFIRMATION[stepNumber] && (
              <button
                type="button"
                onClick={() => setAffirmed((v) => !v)}
                aria-pressed={affirmed}
                className={`flex items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
                  affirmed ? "border-2 border-primary bg-primary/[0.06]" : "border-border"
                }`}
              >
                <span className={`mt-0.5 ${affirmed ? "text-primary" : "text-muted-foreground"}`}>
                  {affirmed ? (
                    <CircleCheck className="h-[18px] w-[18px]" strokeWidth={2} />
                  ) : (
                    <Circle className="h-[18px] w-[18px]" strokeWidth={2} />
                  )}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground">
                    {AFFIRMATION[stepNumber].label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {AFFIRMATION[stepNumber].sub}
                  </span>
                </span>
              </button>
            )}
            <Button
              size="xl"
              fullWidth
              disabled={busy}
              onClick={() =>
                goTo("sponsor", {
                  willingness_note: willingness.trim() || null,
                  // Only steps with an affirmation patch this field — older
                  // deployments of the schema don't have the column yet.
                  ...(AFFIRMATION[stepNumber] ? { willingness_affirmed: affirmed } : {}),
                })
              }
            >
              Continue
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
            <Button
              variant="ghost"
              fullWidth
              className="text-[13px] text-muted-foreground"
              onClick={() => {
                setWillingness("");
                goTo("sponsor", {
                  willingness_note: null,
                  ...(AFFIRMATION[stepNumber] ? { willingness_affirmed: affirmed } : {}),
                });
              }}
            >
              Skip this
            </Button>
          </div>
        )}

        {/* ------------------------------ sponsor ------------------------------ */}
        {phase === "sponsor" && (
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Step {word} · Bring it to your sponsor
              </p>
              <h2 className="mt-1 text-xl font-semibold text-foreground">
                This step is worked together
              </h2>
            </div>
            <p className="text-[15px] leading-relaxed text-foreground">
              {SPONSOR_BODY[stepNumber] ??
                "Share what you've written, or simply let your sponsor know you're ready to talk it through."}
            </p>

            {sponsors.length > 0 ? (
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <HeartHandshake className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {sponsors[0].target_email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Your sponsor · sharing is read-only, revocable anytime
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="px-4 py-3.5">
                  <p className="text-sm text-foreground">No sponsor connected yet.</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    You can still mark this step ready —{" "}
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      onClick={() => navigate("/sharing")}
                    >
                      invite a sponsor
                    </button>{" "}
                    whenever it feels right.
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="flex flex-col gap-2.5">
              {(
                [
                  ["share", `Share my Step ${stepNumber} reflections`, "Your sponsor can read what you wrote — nothing more."],
                  ["nudge", "Just say I'm ready to talk", "No writing is shared. They get a quiet nudge."],
                ] as ["share" | "nudge", string, string][]
              ).map(([key, title, sub]) => {
                const on = shareChoice === key;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setShareChoice(key)}
                    className={`flex items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
                      on ? "border-2 border-primary bg-primary/[0.06]" : "border-border"
                    }`}
                  >
                    <span className={`mt-0.5 ${on ? "text-primary" : "text-muted-foreground"}`}>
                      {on ? (
                        <CircleCheck className="h-[18px] w-[18px]" strokeWidth={2} />
                      ) : (
                        <Circle className="h-[18px] w-[18px]" strokeWidth={2} />
                      )}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground">{title}</span>
                      <span className="block text-xs text-muted-foreground">{sub}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {SPONSOR_PRIVACY_NOTE[stepNumber] && (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
                <Lock className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed text-foreground">
                  {SPONSOR_PRIVACY_NOTE[stepNumber]}
                </p>
              </div>
            )}

            <Button
              size="xl"
              fullWidth
              disabled={busy}
              onClick={() =>
                goTo("close", {
                  is_shared: shareChoice === "share",
                  ready_to_talk: true,
                  status: "ready",
                })
              }
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : (
                `Mark Step ${stepNumber} Ready`
              )}
            </Button>
            <Button
              variant="ghost"
              fullWidth
              className="text-[13px] text-muted-foreground"
              onClick={() => navigate("/steps")}
            >
              Not yet
            </Button>
          </div>
        )}

        {/* ------------------------------- close ------------------------------- */}
        {phase === "close" && (
          <div className="flex flex-col items-center gap-5 pt-10 text-center">
            <LogoMark className="h-14 w-14 animate-scale-in" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {partialClose?.title ?? `You've marked Step ${stepNumber} ready.`}
              </h1>
              <p className="mx-auto mt-2 max-w-[290px] text-[15px] leading-relaxed text-muted-foreground">
                {partialClose?.body ??
                  CLOSE_BODY[stepNumber] ??
                  "Your reflections are saved and private. The next step opens whenever you're ready — no rush, and nothing behind you disappears."}
              </p>
            </div>
            {data.work?.is_shared ? (
              <Badge status="green">
                <Check className="h-[11px] w-[11px]" strokeWidth={2} />
                Shared with your sponsor · read-only
              </Badge>
            ) : null}
            <div className="flex w-full flex-col gap-2">
              <Button size="xl" fullWidth onClick={() => navigate("/steps")}>
                Return to Steps
              </Button>
              <Button variant="ghost" fullWidth onClick={() => navigate("/checkin")}>
                Back to Today's Check-In
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One prompt: its saved answers (with additions) + inputs to add more. */
function PromptBlock({
  stepNumber,
  promptId,
  promptText,
  note,
  answers,
  onSaved,
  onError,
}: {
  stepNumber: number;
  promptId: string;
  promptText: string;
  /** Optional per-prompt note, rendered with a lock icon (Step 6 canvas). */
  note?: string | null;
  answers: StepReflection[];
  onSaved: () => Promise<void> | void;
  onError: (msg: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [addText, setAddText] = useState("");
  const [busy, setBusy] = useState(false);

  async function saveAnswer() {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await addStepReflection(stepNumber, {
        step_prompt_id: promptId,
        prompt_text: promptText,
        answer_text: draft.trim(),
      });
      setDraft("");
      await onSaved();
    } catch {
      onError("Couldn't save your answer. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function saveAddition(reflectionId: string) {
    if (!addText.trim()) return;
    setBusy(true);
    try {
      await addReflectionAddition(reflectionId, addText.trim());
      setAddText("");
      setAddingTo(null);
      await onSaved();
    } catch {
      onError("Couldn't add to your answer. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <p className="text-[15px] font-medium text-foreground">{promptText}</p>
        {note && (
          <div className="flex items-start gap-2.5 rounded-lg border border-border bg-primary/[0.06] px-3.5 py-3">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
            <p className="text-xs leading-relaxed text-foreground">{note}</p>
          </div>
        )}

        {answers.map((a) => (
          <div key={a.id} className="rounded-lg bg-muted/50 p-3">
            <p className="whitespace-pre-wrap text-sm text-foreground">{a.answer_text}</p>
            {a.additions.map((add) => (
              <p
                key={add.id}
                className="mt-2 border-l-2 border-border pl-3 text-sm text-muted-foreground"
              >
                {add.added_text}
              </p>
            ))}
            {addingTo === a.id ? (
              <div className="mt-2 flex flex-col gap-2">
                <Textarea
                  rows={2}
                  placeholder="Add to this answer…"
                  value={addText}
                  onChange={(e) => setAddText(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy} onClick={() => saveAddition(a.id)}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setAddingTo(null);
                      setAddText("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAddingTo(a.id)}
                className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2"
              >
                <Plus className="h-3 w-3" strokeWidth={2} />
                Add to this answer
              </button>
            )}
          </div>
        ))}

        <Textarea
          rows={3}
          placeholder={answers.length ? "Another thought…" : "In your own words…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div>
          <Button size="sm" disabled={busy || !draft.trim()} onClick={saveAnswer}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Save answer"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
